import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { budgetReportingScheduleValidator } from "shared/src/validators";
import { toast } from "sonner";
import type { z } from "zod";
import { FormStepSeparator } from "@/components/ui/form-step-seperator";
import { useAppForm } from "@/hooks/useCreateForm";
import { trpc } from "@/lib/trpcClient";

export const Route = createFileRoute(
  "/_authed/_org/budgets/$groupId/reporting",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { mutate: editReportingSchedule, isPending } = useMutation(
    trpc.budget.editReportingSchedule.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: () => {
        toast.success("Updated the budget's reporting schedule");
        form.reset();
      },
    }),
  );

  const { groupId } = Route.useParams();

  const { data: budgetGroup } = useSuspenseQuery(
    trpc.budget.get.queryOptions({ groupId }),
  );

  const form = useAppForm({
    defaultValues: {
      frequency: budgetGroup.reportingFrequency,
      groupId,
      users: budgetGroup.reportRecipients,
    } as z.infer<typeof budgetReportingScheduleValidator>,
    onSubmit: ({ value }) => editReportingSchedule(value),
    onSubmitInvalid: ({ formApi }) => {
      console.log("Form invalid", formApi.getAllErrors());
    },
    validators: {
      onSubmit: budgetReportingScheduleValidator,
    },
  });

  return (
    <form.AppForm>
      <form
        className="flex h-full flex-col justify-between"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex flex-col px-6 pt-2 pb-4">
          <FormStepSeparator description="Reporting Schedule" />

          <form.AppField
            children={(field) => (
              <field.UserMultipleSelectField
                label="Email Budget Report to"
                descriptionStyle="below"
                required
                className="mb-4"
              />
            )}
            name="users"
          />

          <form.AppField
            children={(field) => {
              return (
                <field.SelectField
                  label="Report Frequency"
                  options={[
                    {
                      description: "Generate a report every Sunday",
                      label: "Weekly",
                      value: "weekly",
                    },
                    {
                      description:
                        "Generate a report on the 1st of every month",
                      label: "Monthly",
                      value: "monthly",
                    },
                    {
                      description: "Generate a report at the end of every year",
                      label: "Annually",
                      value: "annual",
                    },
                  ]}
                  required={true}
                  description={`If you are looking to manually download a report, you can do so from the "Actions" menu in the top right.`}
                  descriptionStyle="below"
                />
              );
            }}
            name="frequency"
          />
        </div>

        <div className="flex min-h-16 flex-row items-center justify-around border-t p-6">
          <form.ConditionalSubmitCancelButtons isPending={isPending} />
        </div>
      </form>
    </form.AppForm>
  );
}
